import { useCallback, useEffect, useState } from "react";
import { api } from "./api";

// A session needs an outcome once its END time plus this grace window has
// passed and it is still marked "scheduled". Not a minute before.
export const NEEDS_OUTCOME_GRACE_MIN = 10;

export function needsOutcomeAt(session, nowMs = Date.now()) {
  if (!session || session.status !== "scheduled" || !session.starts_at) return false;
  const start = new Date(session.starts_at).getTime();
  if (Number.isNaN(start)) return false;
  const end = start + (Number(session.duration_min) || 60) * 60000;
  return nowMs >= end + NEEDS_OUTCOME_GRACE_MIN * 60000;
}

// Ids the trainer pressed "Later" on. Module scope on purpose: cleared on a
// fresh app open (reload / new visit), kept while the tab stays alive so a
// refocus doesn't resurface what was just deferred.
const deferred = new Set();

export const deferNeedsOutcome = (id) => deferred.add(id);
export const isDeferred = (id) => deferred.has(id);

export async function fetchNeedsOutcome() {
  const { data } = await api.get("/sessions");
  const now = Date.now();
  return (data || [])
    .filter((s) => needsOutcomeAt(s, now) && !deferred.has(s.session_id))
    .sort((a, b) => new Date(b.starts_at) - new Date(a.starts_at));
}

export const NEEDS_OUTCOME_OPEN_EVENT = "trainmate:needs-outcome-open";
export const NEEDS_OUTCOME_CHANGED_EVENT = "trainmate:needs-outcome-changed";

export const openNeedsOutcome = () =>
  window.dispatchEvent(new Event(NEEDS_OUTCOME_OPEN_EVENT));

export function useNeedsOutcomeCount() {
  const [count, setCount] = useState(0);

  const refresh = useCallback(async () => {
    try {
      setCount((await fetchNeedsOutcome()).length);
    } catch {
      setCount(0);
    }
  }, []);

  useEffect(() => {
    refresh();
    window.addEventListener(NEEDS_OUTCOME_CHANGED_EVENT, refresh);
    return () => window.removeEventListener(NEEDS_OUTCOME_CHANGED_EVENT, refresh);
  }, [refresh]);

  return count;
}
